<template>
  <div class="enrollment-approval">
    <header class="page-header">
      <div>
        <h2>报名审批</h2>
        <p class="subtitle">审核学员报名，控制课程名额与审批状态</p>
      </div>
    </header>

    <section class="filter-panel">
      <div class="filter-item">
        <label>课程</label>
        <select v-model="filters.courseId">
          <option value="">全部课程</option>
          <option v-for="course in courseOptions" :key="course.id" :value="course.id">
            {{ course.title }}
          </option>
        </select>
      </div>
      <div class="filter-item">
        <label>状态</label>
        <select v-model="filters.status">
          <option value="">全部状态</option>
          <option v-for="option in statusOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
      <div class="filter-item">
        <label>关键字</label>
        <input v-model="filters.keyword" placeholder="学员姓名 / 手机号" />
      </div>
      <div class="filter-actions">
        <button class="primary-button" @click="loadEnrollments">查询</button>
        <button class="ghost-button" @click="resetFilters">重置</button>
        <button class="success-button" @click="exportToExcel" :disabled="loading || pagination.total === 0">
          {{ exporting ? '导出中...' : '导出Excel' }}
        </button>
      </div>
    </section>

    <section class="table-wrapper" v-if="enrollments.length">
      <table>
        <thead>
          <tr>
            <th>学员信息</th>
            <th>课程</th>
            <th>提交时间</th>
            <th>支付方式</th>
            <th>推荐人身份证号</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in enrollments" :key="item.id">
            <td>
              <div class="student-cell">
                <span class="student-name">{{ item.studentName }}</span>
                <span class="student-phone">
                  {{ getDisplayValue(item.id, 'studentPhone', item.studentPhone) }}
                  <button v-if="item.studentPhone" class="decrypt-btn" @click="toggleDecrypt(item.id, 'studentPhone')">
                    <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <template v-if="!isDecrypted(item.id, 'studentPhone')">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </template>
                      <template v-else>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </template>
                    </svg>
                  </button>
                </span>
                <span class="student-id" v-if="item.idNumber">
                  证件：{{ getDisplayValue(item.id, 'idNumber', item.idNumber) }}
                  <button class="decrypt-btn" @click="toggleDecrypt(item.id, 'idNumber')">
                    <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <template v-if="!isDecrypted(item.id, 'idNumber')">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </template>
                      <template v-else>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </template>
                    </svg>
                  </button>
                </span>
              </div>
            </td>
            <td>
              <div class="course-cell">
                <span>{{ item.courseTitle }}</span>
              </div>
            </td>
            <td>{{ formatDate(item.createdAt) }}</td>
            <td>{{ formatPaymentMethod(item.paymentMethod) }}</td>
            <td>
              {{ getDisplayValue(item.id, recommenderSensitiveField(item), recommenderSensitiveValue(item)) || '—' }}
              <button v-if="recommenderSensitiveValue(item)" class="decrypt-btn" @click="toggleDecrypt(item.id, recommenderSensitiveField(item))">
                <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <template v-if="!isDecrypted(item.id, recommenderSensitiveField(item))">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </template>
                  <template v-else>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </template>
                </svg>
              </button>
            </td>
            <td>
              <span :class="['status-tag', item.status?.toLowerCase()]">{{ statusLabel(item.status) }}</span>
            </td>
            <td>
              <div class="action-buttons">
                <button class="link-button" @click="viewDetail(item.id)">详情</button>
                <button
                  class="primary-button small"
                  v-if="item.status === 'PENDING'"
                  @click="openDecision(item, true)"
                >
                  审批通过
                </button>
                <button
                  class="danger-link small"
                  v-if="item.status === 'PENDING'"
                  @click="openDecision(item, false)"
                >
                  拒绝
                </button>
                <button
                  v-if="canChangeStatus(item.status)"
                  class="ghost-button small"
                  @click="openStatusModal(item)"
                >
                  修改状态
                </button>
                <button
                  v-if="item.status === 'DEFERRED'"
                  class="primary-button small"
                  @click="openTransferModal(item)"
                >
                  转班
                </button>
                <button
                  v-if="item.status === 'PAID'"
                  class="danger-link small"
                  @click="openRefundModal(item)"
                >
                  退课
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pagination">
        <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 0 || loading">上一页</button>
        <span>第 {{ pagination.page + 1 }} / {{ totalPages }} 页</span>
        <button @click="changePage(pagination.page + 1)" :disabled="pagination.page + 1 >= totalPages || loading">
          下一页
        </button>
      </div>
    </section>

    <div v-else class="empty-state">
      <p>暂无报名记录</p>
    </div>

    <div v-if="showDecision" class="modal-backdrop">
      <div class="modal">
        <h3>{{ decisionForm.approved ? '审批通过' : '审批拒绝' }}</h3>
        <form @submit.prevent="submitDecision">
          <div class="form-summary">
            <p><strong>学员：</strong>{{ decisionForm.studentName }}（{{ decisionForm.studentPhone }}）</p>
            <p><strong>课程：</strong>{{ decisionForm.courseTitle }}</p>
          </div>
          <label>
            <span>备注</span>
            <textarea rows="3" v-model="decisionForm.remark" placeholder="可选，说明审批备注"></textarea>
          </label>
          <label v-if="!decisionForm.approved">
            <span>拒绝原因</span>
            <textarea rows="4" v-model="decisionForm.rejectionReason" required placeholder="请填写拒绝原因"></textarea>
          </label>
          <div class="modal-actions">
            <button type="button" class="ghost-button" @click="closeDecision">取消</button>
            <button type="submit" class="primary-button" :disabled="loading">
              {{ loading ? '处理中...' : '确认' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showStatusModal" class="modal-backdrop">
      <div class="modal">
        <h3>修改报名状态</h3>
        <div class="form-summary">
          <p><strong>学员：</strong>{{ statusModalForm.studentName }}（{{ statusModalForm.studentPhone }}）</p>
          <p><strong>课程：</strong>{{ statusModalForm.courseTitle }}</p>
          <p><strong>当前状态：</strong>{{ statusLabel(statusModalForm.currentStatus) }}</p>
        </div>
        <label>
          <span>选择新状态</span>
          <select v-model="statusModalForm.newStatus" class="status-select-full">
            <option :value="null">请选择状态</option>
            <option
              v-for="status in getAvailableStatuses(statusModalForm.currentStatus)"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
        </label>
        <div class="modal-actions">
          <button type="button" class="ghost-button" @click="closeStatusModal">取消</button>
          <button
            type="button"
            class="primary-button"
            :disabled="loading || !statusModalForm.newStatus"
            @click="submitStatusUpdate"
          >
            {{ loading ? '处理中...' : '确认' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showTransferModal" class="modal-backdrop">
      <div class="modal">
        <h3>学员转班</h3>
        <div class="form-summary">
          <p><strong>学员：</strong>{{ transferModalForm.studentName }}（{{ transferModalForm.studentPhone }}）</p>
          <p><strong>原课程：</strong>{{ transferModalForm.courseTitle }}</p>
        </div>
        <label>
          <span>选择目标课程</span>
          <select v-model="transferModalForm.newCourseId" class="status-select-full">
            <option :value="null">请选择目标课程</option>
            <option
              v-for="course in transferCourses"
              :key="course.id"
              :value="course.id"
            >
              {{ course.title }}
            </option>
          </select>
          <p class="help-text" v-if="transferCourses.length === 0">暂无符合条件的课程（需有相同标签且正在报名中）</p>
        </label>
        <div class="modal-actions">
          <button type="button" class="ghost-button" @click="closeTransferModal">取消</button>
          <button
            type="button"
            class="primary-button"
            :disabled="loading || !transferModalForm.newCourseId"
            @click="submitTransfer"
          >
            {{ loading ? '处理中...' : '确认转班' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showRefundModal" class="modal-backdrop">
      <div class="modal">
        <h3>学员退课</h3>
        <div class="form-summary">
          <p><strong>学员：</strong>{{ refundModalForm.studentName }}（{{ refundModalForm.studentPhone }}）</p>
          <p><strong>课程：</strong>{{ refundModalForm.courseTitle }}</p>
        </div>
        <div class="checkbox-field">
          <label class="checkbox-label">
            <input type="checkbox" v-model="refundModalForm.noRefundNeeded" />
            <span>无需退费（不需要上传截图）</span>
          </label>
        </div>
        <label v-if="!refundModalForm.noRefundNeeded">
          <span>上传退费截图 <span class="required">*</span></span>
          <input
            type="file"
            accept="image/*"
            @change="handleRefundFileChange"
            class="file-input"
          />
          <p class="help-text">请上传退费截图（支持jpg、jpeg、png、gif、bmp、webp格式）</p>
          <div v-if="refundModalForm.previewUrl" class="image-preview">
            <img :src="refundModalForm.previewUrl" alt="退费截图预览" />
          </div>
        </label>
        <div class="modal-actions">
          <button type="button" class="ghost-button" @click="closeRefundModal">取消</button>
          <button
            type="button"
            class="primary-button"
            :disabled="loading || (!refundModalForm.file && !refundModalForm.noRefundNeeded)"
            @click="submitRefund"
          >
            {{ loading ? '处理中...' : '确认退课' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 抽屉 + 半透明遮罩：点击遮罩或关闭按钮均可关闭 -->
    <div v-if="detail" class="drawer-overlay" @click.self="closeDetail">
      <aside class="drawer" role="dialog" aria-modal="true" @click.stop>
      <div class="drawer-header">
        <h3>报名详情</h3>
        <button type="button" class="close-button" @click.prevent="closeDetail" aria-label="关闭">×</button>
      </div>
      <div class="drawer-body">
        <div class="drawer-content">
        <section>
          <h4>课程信息</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">课程：</span>
              <span class="info-value">{{ detail.courseTitle || '—' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">状态：</span>
              <span class="info-value">{{ statusLabel(detail.status) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">提交时间：</span>
              <span class="info-value">{{ formatDate(detail.createdAt) }}</span>
            </div>
            <div class="info-item" v-if="detail.auditAt">
              <span class="info-label">审批时间：</span>
              <span class="info-value">{{ formatDate(detail.auditAt) }}</span>
            </div>
            <div class="info-item" v-if="detail.auditBy">
              <span class="info-label">审批人：</span>
              <span class="info-value">{{ detail.auditBy }}</span>
            </div>
          </div>
        </section>
        <section>
          <h4>学员信息</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">姓名：</span>
              <span class="info-value">{{ detail.studentName || '—' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">手机：</span>
              <span class="info-value">
                {{ getDisplayValue(detail.id, 'studentPhone', detail.studentPhone) || '—' }}
                <button v-if="detail.studentPhone" class="decrypt-btn" @click="toggleDecrypt(detail.id, 'studentPhone')">
                  <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <template v-if="!isDecrypted(detail.id, 'studentPhone')">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </template>
                    <template v-else>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </template>
                  </svg>
                </button>
              </span>
            </div>
            <div class="info-item" v-if="detail.idNumber">
              <span class="info-label">身份证号：</span>
              <span class="info-value">
                {{ getDisplayValue(detail.id, 'idNumber', detail.idNumber) }}
                <button class="decrypt-btn" @click="toggleDecrypt(detail.id, 'idNumber')">
                  <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <template v-if="!isDecrypted(detail.id, 'idNumber')">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </template>
                    <template v-else>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </template>
                  </svg>
                </button>
              </span>
            </div>
            <div class="info-item" v-if="detail.sex">
              <span class="info-label">性别：</span>
              <span class="info-value">{{ detail.sex }}</span>
            </div>
            <div class="info-item" v-if="detail.ethnicity">
              <span class="info-label">民族：</span>
              <span class="info-value">{{ detail.ethnicity }}</span>
            </div>
            <div class="info-item" v-if="detail.birthDate">
              <span class="info-label">出生年月：</span>
              <span class="info-value">{{ detail.birthDate }}</span>
            </div>
            <div class="info-item" v-if="detail.idCardAddress">
              <span class="info-label">身份证地址：</span>
              <span class="info-value">{{ detail.idCardAddress }}</span>
            </div>
            <div class="info-item" v-if="detail.currentName">
              <span class="info-label">现用名：</span>
              <span class="info-value">{{ detail.currentName }}</span>
            </div>
            <div class="info-item" v-if="detail.qqNumber">
              <span class="info-label">QQ号：</span>
              <span class="info-value">{{ detail.qqNumber }}</span>
            </div>
            <div class="info-item" v-if="detail.wechatNumber">
              <span class="info-label">微信号：</span>
              <span class="info-value">{{ detail.wechatNumber }}</span>
            </div>
            <div class="info-item" v-if="detail.contactAddress">
              <span class="info-label">联系地址：</span>
              <span class="info-value">{{ detail.contactAddress }}</span>
            </div>
            <div class="info-item" v-if="detail.healthStatus">
              <span class="info-label">健康状况：</span>
              <span class="info-value">{{ detail.healthStatus }}</span>
            </div>
            <div class="info-item" v-if="detail.problemDescription">
              <span class="info-label">需要解决的问题：</span>
              <span class="info-value">{{ detail.problemDescription }}</span>
            </div>
            <div class="info-item" v-if="recommenderSensitiveValue(detail)">
              <span class="info-label">推荐人身份证号：</span>
              <span class="info-value">
                {{ getDisplayValue(detail.id, recommenderSensitiveField(detail), recommenderSensitiveValue(detail)) }}
                <button class="decrypt-btn" @click="toggleDecrypt(detail.id, recommenderSensitiveField(detail))">
                  <svg class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <template v-if="!isDecrypted(detail.id, recommenderSensitiveField(detail))">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </template>
                    <template v-else>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </template>
                  </svg>
                </button>
              </span>
            </div>
            <div class="info-item" v-if="detail.recommenderSuggestion">
              <span class="info-label">推荐建议：</span>
              <span class="info-value">{{ detail.recommenderSuggestion }}</span>
            </div>
          </div>
        </section>
        <section>
          <h4>报名信息</h4>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">支付方式：</span>
              <span class="info-value">{{ formatPaymentMethod(detail.paymentMethod) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">待支付金额：</span>
              <span class="info-value">¥{{ detail.amountDue }}</span>
            </div>
            <div class="info-item" v-if="detail.paymentMethod === 'quota'">
              <span class="info-label">需使用推荐名额：</span>
              <span class="info-value">{{ detail.deduction || 0 }}</span>
            </div>
            <div class="info-item" v-if="detail.remark">
              <span class="info-label">备注：</span>
              <span class="info-value">{{ detail.remark }}</span>
            </div>
            <div class="info-item" v-if="detail.rejectionReason">
              <span class="info-label">拒绝原因：</span>
              <span class="info-value">{{ detail.rejectionReason }}</span>
            </div>
            <div class="info-item" v-if="detail.recommenderSuggestion">
              <span class="info-label">推荐建议：</span>
              <span class="info-value">{{ detail.recommenderSuggestion }}</span>
            </div>
            <div class="info-item" v-if="detail.refundScreenshotUrl">
              <span class="info-label">退费截图：</span>
              <div class="info-value">
                <img :src="detail.refundScreenshotUrl" alt="退费截图" class="refund-screenshot" @click="viewImage(detail.refundScreenshotUrl)" />
              </div>
            </div>
          </div>
        </section>
        </div>
      </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { enrollmentService } from '@/services/enrollmentService'
import { courseService } from '@/services/courseService'
import { sensitiveDataService } from '../services/sensitiveDataService'
import * as XLSX from 'xlsx'

const STATUS_MAP = {
  PENDING: '待审核',
  PENDING_RECOMMENDER: '等待推荐人意见',
  APPROVED: '已审批',
  REJECTED: '已拒绝',
  PENDING_PAYMENT: '待付款',
  PAID: '已付款',
  DEFERRED: '已延期',
  COMPLETED: '已结课',
  CANCELLED: '已取消',
  REFUNDED: '已退课'
}

export default {
  name: 'EnrollmentApproval',
  data() {
    return {
      enrollments: [],
      loading: false,
      filters: {
        courseId: '',
        status: '',
        keyword: ''
      },
      pagination: {
        page: 0,
        size: 10,
        total: 0
      },
      courseOptions: [],
      showDecision: false,
      decisionForm: {
        id: null,
        approved: true,
        remark: '',
        rejectionReason: '',
        deduction: 0,
        studentName: '',
        studentPhone: '',
        courseTitle: ''
      },
      detail: null,
      statusOptions: Object.entries(STATUS_MAP).map(([value, label]) => ({ value, label })),
      showStatusModal: false,
      statusModalForm: {
        id: null,
        studentName: '',
        studentPhone: '',
        courseTitle: '',
        currentStatus: null,
        newStatus: null
      },
      showTransferModal: false,
      transferModalForm: {
        id: null,
        studentName: '',
        studentPhone: '',
        courseTitle: '',
        newCourseId: null
      },
      transferCourses: [],
      exporting: false,
      decryptedValues: {},
      showRefundModal: false,
      refundModalForm: {
        id: null,
        studentName: '',
        studentPhone: '',
        courseTitle: '',
        file: null,
        previewUrl: null,
        noRefundNeeded: false
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1
    }
  },
  created() {
    this.loadCourses()
    // 从路由查询参数中读取课程ID
    const courseId = this.$route.query.courseId
    if (courseId) {
      this.filters.courseId = courseId
    }
    this.loadEnrollments()
  },
  methods: {
    recommenderSensitiveField(enrollment) {
      return enrollment?.recommenderIdNumber ? 'recommenderIdNumber' : 'recommenderPhone'
    },
    recommenderSensitiveValue(enrollment) {
      return enrollment?.recommenderIdNumber || enrollment?.recommenderPhone || ''
    },
    statusLabel(status) {
      return STATUS_MAP[status] || status || '—'
    },
    formatDate(value) {
      if (!value) return '—'
      return value.replace('T', ' ').replace('Z', '')
    },
    formatPaymentMethod(method) {
      if (!method) return '—'
      if (method === 'offline') return '线下支付'
      if (method === 'quota') return '推荐名额抵扣'
      if (method === 'quota,offline' || method === 'offline,quota') return '推荐名额抵扣 + 线下支付'
      return method
    },
    async loadCourses() {
      try {
        const response = await courseService.fetchCourses({ page: 0, size: 100 })
        if (response.success) {
          this.courseOptions = response.data.content || []
        }
      } catch (error) {
        console.error('加载课程失败', error)
      }
    },
    async loadEnrollments() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          size: this.pagination.size,
          courseId: this.filters.courseId || undefined,
          status: this.filters.status || undefined,
          keyword: this.filters.keyword || undefined
        }
        const response = await enrollmentService.fetchEnrollments(params)
        if (response.success) {
          this.enrollments = response.data.content || []
          this.pagination.total = response.data.totalElements || 0
        }
      } catch (error) {
        console.error('加载报名记录失败', error)
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = {
        courseId: '',
        status: '',
        keyword: ''
      }
      this.pagination.page = 0
      this.loadEnrollments()
    },
    changePage(page) {
      if (page < 0) return
      if (page >= this.totalPages) return
      this.pagination.page = page
      this.loadEnrollments()
    },
    openDecision(item, approved) {
      this.decisionForm = {
        id: item.id,
        approved,
        remark: '',
        rejectionReason: '',
        deduction: item.deduction || 0,
        studentName: item.studentName,
        studentPhone: item.studentPhone,
        courseTitle: item.courseTitle
      }
      this.showDecision = true
    },
    closeDecision() {
      this.showDecision = false
      this.decisionForm = {
        id: null,
        approved: true,
        remark: '',
        rejectionReason: '',
        deduction: 0,
        studentName: '',
        studentPhone: '',
        courseTitle: ''
      }
    },
    async submitDecision() {
      if (!this.decisionForm.id) return
      if (!this.decisionForm.approved && !this.decisionForm.rejectionReason) {
        alert('请填写拒绝原因')
        return
      }
      try {
        this.loading = true
        const payload = {
          approved: this.decisionForm.approved,
          remark: this.decisionForm.remark,
          rejectionReason: this.decisionForm.approved ? null : this.decisionForm.rejectionReason
        }
        await enrollmentService.reviewEnrollment(this.decisionForm.id, payload)
        this.closeDecision()
        this.loadEnrollments()
      } catch (error) {
        console.error('审批失败', error)
      } finally {
        this.loading = false
      }
    },
    async viewDetail(id) {
      try {
        const response = await enrollmentService.fetchEnrollmentDetail(id)
        if (response.success) {
          this.detail = response.data
        }
      } catch (error) {
        console.error('获取报名详情失败', error)
      }
    },
    closeDetail() {
      this.detail = null
    },
    canChangeStatus(status) {
      // 已结课、已取消和已退课状态不能再更改
      return status !== 'COMPLETED' && status !== 'CANCELLED' && status !== 'REFUNDED'
    },
    getAvailableStatuses(currentStatus) {
      const statusList = []
      switch (currentStatus) {
        case 'PENDING_PAYMENT':
          statusList.push({ value: 'PAID', label: '已付款' })
          statusList.push({ value: 'CANCELLED', label: '已取消' })
          break
        case 'PAID':
          statusList.push({ value: 'DEFERRED', label: '已延期' })
          statusList.push({ value: 'COMPLETED', label: '已结课' })
          statusList.push({ value: 'CANCELLED', label: '已取消' })
          statusList.push({ value: 'REFUNDED', label: '已退课' })
          break
        case 'DEFERRED':
          statusList.push({ value: 'CANCELLED', label: '已取消' })
          break
        case 'APPROVED':
          // 兼容旧数据
          statusList.push({ value: 'PENDING_PAYMENT', label: '待付款' })
          statusList.push({ value: 'PAID', label: '已付款' })
          statusList.push({ value: 'CANCELLED', label: '已取消' })
          break
        case 'REJECTED':
          // 已拒绝状态可以改为已取消
          statusList.push({ value: 'CANCELLED', label: '已取消' })
          break
        default:
          // 其他状态（除了PENDING）可以改为已取消
          if (currentStatus !== 'PENDING') {
            statusList.push({ value: 'CANCELLED', label: '已取消' })
          }
      }
      return statusList
    },
    openStatusModal(item) {
      this.statusModalForm = {
        id: item.id,
        studentName: item.studentName,
        studentPhone: item.studentPhone,
        courseTitle: item.courseTitle,
        currentStatus: item.status,
        newStatus: null
      }
      this.showStatusModal = true
    },
    closeStatusModal() {
      this.showStatusModal = false
      this.statusModalForm = {
        id: null,
        studentName: '',
        studentPhone: '',
        courseTitle: '',
        currentStatus: null,
        newStatus: null
      }
    },
    async submitStatusUpdate() {
      if (!this.statusModalForm.newStatus) {
        alert('请选择新状态')
        return
      }
      
      try {
        this.loading = true
        const response = await enrollmentService.updateEnrollmentStatus(this.statusModalForm.id, {
          status: this.statusModalForm.newStatus
        })
        if (response.success) {
          this.closeStatusModal()
          this.loadEnrollments()
        }
      } catch (error) {
        console.error('更新状态失败', error)
        alert(error.response?.data?.message || '更新状态失败')
      } finally {
        this.loading = false
      }
    },
    async openTransferModal(item) {
      this.transferModalForm = {
        id: item.id,
        studentName: item.studentName,
        studentPhone: item.studentPhone,
        courseTitle: item.courseTitle,
        newCourseId: null
      }
      
      // 筛选可转班的课程（这里简单处理，前端筛选，实际可能需要后端接口支持更精准的筛选）
      // 假设courseOptions包含了所有课程，我们需要筛选出：
      // 1. 状态为ENROLLING
      // 2. 不是当前课程
      // 3. 有相同标签（这个前端暂时拿不到标签信息，只能先列出所有报名中的课程，由后端校验）
      // 为了更好的体验，最好后端提供一个接口查询可转班课程，或者在列表接口返回tags
      // 这里暂时列出所有报名中的课程，除了当前课程
      
      // 由于courseOptions可能不全（分页），最好重新获取所有报名中的课程
      // 或者简单点，直接用当前已加载的courseOptions筛选
      this.transferCourses = this.courseOptions.filter(c => 
        c.id !== item.courseId && c.status === 'ENROLLING'
      )
      
      this.showTransferModal = true
    },
    closeTransferModal() {
      this.showTransferModal = false
      this.transferModalForm = {
        id: null,
        studentName: '',
        studentPhone: '',
        courseTitle: '',
        newCourseId: null
      }
      this.transferCourses = []
    },
    async submitTransfer() {
      if (!this.transferModalForm.newCourseId) {
        alert('请选择目标课程')
        return
      }
      
      try {
        this.loading = true
        await enrollmentService.transferEnrollment(this.transferModalForm.id, {
          newCourseId: this.transferModalForm.newCourseId
        })
        this.closeTransferModal()
        this.loadEnrollments()
        alert('转班成功')
      } catch (error) {
        console.error('转班失败', error)
        alert(error.response?.data?.message || '转班失败')
      } finally {
        this.loading = false
      }
    },
    openRefundModal(item) {
      this.refundModalForm = {
        id: item.id,
        studentName: item.studentName,
        studentPhone: item.studentPhone,
        courseTitle: item.courseTitle,
        file: null,
        previewUrl: null
      }
      this.showRefundModal = true
    },
    closeRefundModal() {
      this.showRefundModal = false
      this.refundModalForm = {
        id: null,
        studentName: '',
        studentPhone: '',
        courseTitle: '',
        file: null,
        previewUrl: null,
        noRefundNeeded: false
      }
    },
    handleRefundFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        // 验证文件类型
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp']
        if (!validTypes.includes(file.type)) {
          alert('文件格式不支持，请上传图片文件（jpg、jpeg、png、gif、bmp、webp）')
          event.target.value = ''
          return
        }
        
        // 验证文件大小（限制为5MB）
        if (file.size > 5 * 1024 * 1024) {
          alert('文件大小不能超过5MB')
          event.target.value = ''
          return
        }
        
        this.refundModalForm.file = file
        
        // 创建预览URL
        const reader = new FileReader()
        reader.onload = (e) => {
          this.refundModalForm.previewUrl = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    async submitRefund() {
      if (!this.refundModalForm.file && !this.refundModalForm.noRefundNeeded) {
        alert('请上传退费截图或勾选无需退费')
        return
      }
      
      try {
        this.loading = true
        const response = await enrollmentService.refundEnrollment(
          this.refundModalForm.id, 
          this.refundModalForm.file,
          this.refundModalForm.noRefundNeeded
        )
        if (response.success) {
          this.closeRefundModal()
          this.loadEnrollments()
          alert('退课成功')
        } else {
          // 如果响应格式不符合预期，显示错误信息
          const errorMsg = response.message || response.error || '退课失败'
          alert(errorMsg)
        }
      } catch (error) {
        console.error('退课失败', error)
        alert(error.response?.data?.message || error.response?.data?.error || '退课失败')
      } finally {
        this.loading = false
      }
    },
    async exportToExcel() {
      try {
        this.exporting = true
        
        // 获取所有筛选后的报名数据
        const params = {
          courseId: this.filters.courseId || undefined,
          status: this.filters.status || undefined,
          keyword: this.filters.keyword || undefined
        }
        const response = await enrollmentService.fetchAllEnrollments(params)
        
        if (!response.success || !response.data || !response.data.content) {
          alert('获取报名数据失败，请重试')
          return
        }

        const allEnrollments = response.data.content || []
        
        if (allEnrollments.length === 0) {
          alert('没有可导出的数据')
          return
        }

        // 准备Excel数据
        const excelData = allEnrollments.map(enrollment => ({
          '学员姓名': enrollment.studentName || '',
          '学员手机号': enrollment.studentPhone || '',
          '身份证号': enrollment.idNumber || '',
          '课程名称': enrollment.courseTitle || '',
          '提交时间': this.formatDate(enrollment.createdAt),
          '支付方式': this.formatPaymentMethod(enrollment.paymentMethod),
          '推荐人身份证号': enrollment.recommenderIdNumber || enrollment.recommenderPhone || '',
          '状态': this.statusLabel(enrollment.status),
          '备注': enrollment.remark || '',
          '拒绝原因': enrollment.rejectionReason || ''
        }))

        // 创建工作簿
        const worksheet = XLSX.utils.json_to_sheet(excelData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, '报名审批')

        // 设置列宽
        const columnWidths = [
          { wch: 12 }, // 学员姓名
          { wch: 15 }, // 学员手机号
          { wch: 20 }, // 身份证号
          { wch: 25 }, // 课程名称
          { wch: 20 }, // 提交时间
          { wch: 12 }, // 支付方式
          { wch: 15 }, // 推荐人手机号
          { wch: 15 }, // 状态
          { wch: 30 }, // 备注
          { wch: 30 }  // 拒绝原因
        ]
        worksheet['!cols'] = columnWidths

        // 生成文件名（包含筛选条件和日期）
        const now = new Date()
        const dateStr = now.toISOString().split('T')[0]
        let fileName = `报名审批_${dateStr}`
        
        // 添加筛选条件到文件名
        if (this.filters.courseId) {
          const course = this.courseOptions.find(c => c.id == this.filters.courseId)
          if (course) {
            fileName += `_${course.title}`
          }
        }
        if (this.filters.status) {
          fileName += `_${this.statusLabel(this.filters.status)}`
        }
        if (this.filters.keyword) {
          fileName += `_${this.filters.keyword}`
        }
        
        fileName += '.xlsx'

        // 导出文件
        XLSX.writeFile(workbook, fileName)
        
        alert(`成功导出 ${allEnrollments.length} 条报名记录`)
      } catch (error) {
        console.error('导出Excel失败', error)
        alert('导出失败，请重试')
      } finally {
        this.exporting = false
      }
    },
    viewImage(url) {
      if (url) {
        window.open(url, '_blank')
      }
    },
    async toggleDecrypt(entityId, field, entityType = 'ENROLLMENT') {
      const key = `${entityType}_${entityId}_${field}`
      if (this.decryptedValues[key]) {
        delete this.decryptedValues[key]
        return
      }
      try {
        const value = await sensitiveDataService.decryptField(entityType, entityId, field)
        if (value !== undefined && value !== null) {
          this.decryptedValues[key] = value
        }
      } catch (error) {
        console.error('解密失败:', error)
        this.$message ? this.$message.error('解密失败：' + (error.message || '未知错误')) : alert('解密失败：' + (error.message || '未知错误'))
      }
    },
    getDisplayValue(entityId, field, maskedValue, entityType = 'ENROLLMENT') {
      const key = `${entityType}_${entityId}_${field}`
      return this.decryptedValues[key] || maskedValue
    },
    isDecrypted(entityId, field, entityType = 'ENROLLMENT') {
      const key = `${entityType}_${entityId}_${field}`
      return !!this.decryptedValues[key]
    }
  }
}
</script>

<style scoped>
.enrollment-approval {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.subtitle {
  color: #6b7280;
  margin-top: 0.25rem;
}

.filter-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  background: #fff;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-item label {
  font-size: 0.85rem;
  color: #4b5563;
}

.filter-item input,
.filter-item select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-sizing: border-box;
  height: 2.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
}

.table-wrapper {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  background: #f9fafb;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

tbody td {
  padding: 0.8rem 1rem;
  border-top: 1px solid #f1f5f9;
  vertical-align: top;
  text-align: left;
}

.student-cell,
.course-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.student-name {
  font-weight: 600;
  color: #111827;
}

.student-phone,
.student-id {
  font-size: 0.85rem;
  color: #6b7280;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-tag.pending {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.status-tag.pending_recommender {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.status-tag.approved {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-tag.rejected {
  background: rgba(248, 113, 113, 0.15);
  color: #dc2626;
}

.status-tag.pending_payment {
  background: rgba(251, 191, 36, 0.15);
  color: #d97706;
}

.status-tag.pending_recommender {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.status-tag.paid {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}

.status-tag.deferred {
  background: rgba(124, 58, 237, 0.15);
  color: #7c3aed;
}


.status-tag.completed {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}

.status-tag.cancelled {
  background: rgba(107, 114, 128, 0.15);
  color: #6b7280;
}

.status-tag.refunded {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-select-full {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  background: #fff;
  cursor: pointer;
}

.primary-button,
.ghost-button,
.success-button,
.link-button,
.danger-link {
  cursor: pointer;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.primary-button {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  padding: 0.55rem 1.2rem;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.25);
}

.primary-button.small {
  padding: 0.35rem 0.8rem;
  font-size: 0.85rem;
  box-shadow: none;
}

.ghost-button {
  background: #f3f4f6;
  color: #374151;
  padding: 0.55rem 1.2rem;
}

.success-button {
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  padding: 0.55rem 1.2rem;
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.25);
}

.success-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.link-button {
  background: transparent;
  color: #2563eb;
  padding: 0.25rem 0.5rem;
}

.danger-link {
  background: transparent;
  color: #ef4444;
  padding: 0.25rem 0.5rem;
}

.danger-link.small {
  font-size: 0.85rem;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.empty-state {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 90;
  padding: 1rem;
}

.modal {
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal textarea,
.modal input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
}

.modal textarea {
  resize: vertical;
  width: calc(100% - 1.5rem);
  box-sizing: border-box;
}

.modal label {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.modal label span {
  font-weight: 500;
  color: #374151;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.form-summary {
  background: #f3f4f6;
  padding: 0.75rem;
  border-radius: 8px;
  color: #4b5563;
  font-size: 0.9rem;
}

.drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  box-sizing: border-box;
}

.drawer {
  width: min(420px, 100vw);
  max-width: 100%;
  height: 100vh;
  background: #fff;
  box-shadow: -12px 0 32px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  flex-shrink: 0;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.5rem;
  padding-bottom: 4rem;
  min-height: 0;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.drawer-body section {
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 8px;
  color: #4b5563;
  flex-shrink: 0;
}

.drawer-body h4 {
  margin-bottom: 0.5rem;
  color: #111827;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  text-align: left;
}

.info-label {
  min-width: 120px;
  font-weight: 500;
  color: #374151;
  flex-shrink: 0;
}

.info-value {
  color: #111827;
  text-align: left;
  word-break: break-word;
}

.close-button {
  background: transparent;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  flex-shrink: 0;
}

.close-button:focus-visible {
  outline: 2px solid var(--color-primary-deep, #2563eb);
  outline-offset: 2px;
  border-radius: 4px;
}

.required {
  color: #ef4444;
}

.file-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  box-sizing: border-box;
}

.help-text {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.image-preview {
  margin-top: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem;
  background: #f9fafb;
}

.image-preview img {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
  cursor: pointer;
}

.refund-screenshot {
  max-width: 200px;
  max-height: 200px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #e5e7eb;
}

.refund-screenshot:hover {
  opacity: 0.8;
}

@media (max-width: 900px) {
  .drawer {
    width: 100%;
  }
}

.checkbox-field {
  margin-bottom: 1rem;
}

.checkbox-label {
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input {
  width: auto !important;
  margin: 0;
}

.decrypt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  margin-left: 4px;
  color: #6b7280;
  border-radius: 4px;
  transition: color 0.2s ease, background 0.2s ease;
  vertical-align: middle;
}

.decrypt-btn:hover {
  color: #2563eb;
  background: rgba(37, 99, 235, 0.08);
}

.eye-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}
</style>

